import { Suspense, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PageHeaderProvider } from "@/contexts/PageHeaderContext";
import GmailComposeHost from "@/features/gmail/components/GmailComposeHost";
import GmailOAuthReturn from "@/features/gmail/components/GmailOAuthReturn";
import IncomingCallHost from "@/features/calls/live/IncomingCallHost";
import LeadsPrefetch from "@/features/leads/api/prefetchLeadsList";
import AIChatPanel from "@/features/ai/components/AIChatPanel";
import ShuffleSummaryModal from "@/features/notifications/components/ShuffleSummaryModal";
import ShuffleWarningModal from "@/features/notifications/components/ShuffleWarningModal";
import RecaptureAlertHost from "@/features/notifications/recapture/RecaptureAlertHost";
import AlertAnnounceHost from "@/features/alerts/announce/AlertAnnounceHost";
import SourceReconnectAlertHost from "@/features/sourceAndCampaign/components/reconnect/SourceReconnectAlertHost";
import ChatPopupAlertHost from "@/features/chat/alerts/ChatPopupAlertHost";
import FollowUpNotifier from "@/features/leads/components/FollowUpNotifier";
import InstallmentDueNotifier from "@/features/leads/components/InstallmentDueNotifier";
import CheckInGate from "@/features/checkIn/components/CheckInGate";
import XaleBar from "./nav/XaleBar";
import MobileLeadHost from "./lead/MobileLeadHost";
import MobileSheetsHost from "./sheets/MobileSheetsHost";
import InternalThreadHost from "./screens/chat/InternalThread";
import { ensureMobileFont, M } from "./theme";
import "./mobile.css";

/**
 * The phone shell: the mobile app's chrome around the same CRM. Screens own
 * their scrolling and leave the bar's slot free at the bottom; the floating
 * bar, the full-screen lead and the app-wide sheets sit above them. Every
 * popup host the desktop layout mounts is mounted here too, so a follow-up
 * due, an alert, an incoming call or a check-in prompt behaves the same on
 * a phone.
 */
export default function MobileLayout() {
  useEffect(() => {
    ensureMobileFont();
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = M.bg;
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);

  return (
    <SidebarProvider defaultOpen={false}>
      <TooltipProvider>
        <PageHeaderProvider>
          <div className="xm min-h-[100dvh] w-full" style={{ backgroundColor: M.bg }}>
            <Suspense
              fallback={
                <div className="flex h-[100dvh] items-center justify-center">
                  <div className="loader" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
            <XaleBar />
            <MobileLeadHost />
            <InternalThreadHost />
            <MobileSheetsHost />
          </div>
          <GmailComposeHost />
          <GmailOAuthReturn />
          <IncomingCallHost />
          <LeadsPrefetch />
          <AIChatPanel />
          <ShuffleSummaryModal />
          <ShuffleWarningModal />
          <RecaptureAlertHost />
          <AlertAnnounceHost />
          <SourceReconnectAlertHost />
          <ChatPopupAlertHost />
          <FollowUpNotifier />
          <InstallmentDueNotifier />
          <CheckInGate />
        </PageHeaderProvider>
      </TooltipProvider>
    </SidebarProvider>
  );
}
